import React, { useEffect, useState } from 'react';
import {
  handleApiError,
  versionPolicyAPI,
  type VersionPolicyState,
} from '../services/api';
import './Dashboard.css';
import './VersionPolicy.css';

// Mirrors the backend regex (^\d{1,4}(\.\d{1,4}){0,3}$). "0.0.0" disables.
const VERSION_PATTERN = /^\d{1,4}(\.\d{1,4}){0,3}$/;

const VersionPolicy: React.FC = () => {
  const [ios, setIos] = useState<string>('0.0.0');
  const [android, setAndroid] = useState<string>('0.0.0');
  const [source, setSource] = useState<'redis' | 'config'>('config');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await versionPolicyAPI.getStatus();
      setIos(data.ios_min_version);
      setAndroid(data.android_min_version);
      setSource(data.source);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const iosValid = VERSION_PATTERN.test(ios);
  const androidValid = VERSION_PATTERN.test(android);
  const canSave = iosValid && androidValid && !loading && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    const payload: VersionPolicyState = {
      ios_min_version: ios,
      android_min_version: android,
    };
    try {
      const { data } = await versionPolicyAPI.setStatus(payload);
      setIos(data.ios_min_version);
      setAndroid(data.android_min_version);
      setSource(data.source);
      setSavedAt(new Date());
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const disableGating = async () => {
    setIos('0.0.0');
    setAndroid('0.0.0');
    setSaving(true);
    setError(null);
    try {
      const { data } = await versionPolicyAPI.setStatus({
        ios_min_version: '0.0.0',
        android_min_version: '0.0.0',
      });
      setIos(data.ios_min_version);
      setAndroid(data.android_min_version);
      setSource(data.source);
      setSavedAt(new Date());
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const gatingActive = ios !== '0.0.0' || android !== '0.0.0';

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h2>Версии за принудително обновяване</h2>
          <p className="last-updated">
            Под този праг клиентите получават блокиращ екран с линк към App Store / Play Store.
            Промените стигат до устройствата при следващото зареждане на конфигурацията (до 5 минути).
          </p>
        </div>
      </div>

      {error && (
        <div className="version-error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
        </div>
      )}

      <div className={`version-status ${gatingActive ? 'on' : 'off'}`}>
        <div className="version-status-icon">
          <i className={gatingActive ? 'fas fa-shield-alt' : 'fas fa-check-circle'}></i>
        </div>
        <div className="version-status-text">
          <div className="version-status-title">
            {loading
              ? 'Зареждане…'
              : gatingActive
                ? 'Активно изискване за версия'
                : 'Без изискване за версия'}
          </div>
          <div className="version-status-sub">
            Източник:{' '}
            <code>{source === 'redis' ? 'admin (Redis)' : 'config (env)'}</code>
            {' · '}
            Използвайте <code>0.0.0</code>, за да изключите гейтинга.
          </div>
        </div>
      </div>

      <div className="version-form">
        <label className="version-field">
          <span>iOS — минимална версия</span>
          <input
            type="text"
            value={ios}
            onChange={(e) => setIos(e.target.value.trim())}
            placeholder="напр. 1.4.0"
            disabled={loading || saving}
            className={iosValid ? '' : 'invalid'}
          />
          {!iosValid && (
            <span className="field-hint error">
              Формат: X, X.Y, X.Y.Z или X.Y.Z.W (до 4 числа). Без префикс „v“.
            </span>
          )}
        </label>

        <label className="version-field">
          <span>Android — минимална версия</span>
          <input
            type="text"
            value={android}
            onChange={(e) => setAndroid(e.target.value.trim())}
            placeholder="напр. 1.4.0"
            disabled={loading || saving}
            className={androidValid ? '' : 'invalid'}
          />
          {!androidValid && (
            <span className="field-hint error">
              Формат: X, X.Y, X.Y.Z или X.Y.Z.W (до 4 числа).
            </span>
          )}
        </label>

        <div className="version-actions">
          <button
            className="btn btn-primary"
            onClick={save}
            disabled={!canSave}
          >
            {saving ? 'Запазване…' : 'Запази'}
          </button>
          {gatingActive && (
            <button
              className="btn btn-secondary"
              onClick={disableGating}
              disabled={loading || saving}
            >
              Изключи гейтинга (0.0.0)
            </button>
          )}
          {savedAt && (
            <span className="version-saved">
              <i className="fas fa-check"></i>
              Запазено в {savedAt.toLocaleTimeString('bg-BG')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default VersionPolicy;
